import { useEffect, useState } from 'react';
import { Workbook } from '@fortune-sheet/react';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { fileBytesToFortuneSheets, toCelldataSheet } from './converters';
import { FortuneSheet } from './types';
import { MIN_ROW_HEIGHT } from './fileTypes';
import styles from './SheetView.module.scss';
import '@fortune-sheet/react/dist/index.css';

type SheetViewSource =
    | { fileUrl: string; sheets?: never }
    | {
          /** In-memory sheets, when already resolved by the caller. Skips the fetch. */
          sheets: FortuneSheet[];
          fileUrl?: never;
      };

export type SheetViewProps = SheetViewSource & {
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Original file name — used only as the workbook's remount key on the fetch path. */
    fileName?: string;
};

/**
 * Read-only inline Fortune-Sheet grid — the universal spreadsheet PREVIEW
 * (DocumentViewer's spreadsheet branch, and any caller with in-memory sheets).
 * No drawer chrome, no download/save — those stay on the editable
 * SheetEditorDrawer. Relies entirely on Fortune-Sheet's own canvas
 * virtualization for large sheets — no row/col preview cap is reimposed here.
 */
const SheetView: React.FC<SheetViewProps> = ({ fileUrl, sheets: inMemorySheets, mimeType, fileName }) => {
    const [fetchedSheets, setFetchedSheets] = useState<FortuneSheet[] | null>(null);
    const [loading, setLoading] = useState(!inMemorySheets);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (inMemorySheets) {
            setLoading(false);
            setError(null);
            return;
        }
        if (!fileUrl) return;

        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setFetchedSheets(null);

        (async () => {
            try {
                const response = await fetch(fileUrl, { signal: controller.signal });
                if (!response.ok) throw new Error('Failed to fetch file');
                const buffer = await response.arrayBuffer();
                const parsed = await fileBytesToFortuneSheets(buffer, mimeType, fileName);
                setFetchedSheets(parsed);
                setLoading(false);
            } catch (err) {
                if ((err as DOMException)?.name === 'AbortError') return;
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [fileUrl, mimeType, fileName, inMemorySheets]);

    // Normalise to celldata: a saved/round-tripped sheet may carry only the
    // runtime 2D `data` (from getAllSheets()), which <Workbook> can't
    // initialise from — mirrors useSheetEditorDrawerLogic's same guard.
    const rawSheets = inMemorySheets ?? fetchedSheets;
    const sheets = rawSheets?.map(toCelldataSheet);

    if (loading) {
        return (
            <div className={styles.statusBox}>
                <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
            </div>
        );
    }

    if (error || !sheets?.length) {
        return (
            <div className={styles.statusBox}>
                <p>{error ? 'Unable to preview this file' : 'This file has no data to preview'}</p>
            </div>
        );
    }

    return (
        <div className={styles.sheetViewWrapper}>
            <Workbook
                key={fileUrl ?? fileName}
                data={sheets}
                showToolbar
                showFormulaBar
                showSheetTabs
                allowEdit={false}
                defaultRowHeight={MIN_ROW_HEIGHT}
            />
        </div>
    );
};

export default SheetView;
